import { useTranslation } from "next-i18next";

import { useMerchantName } from "@/common/hooks/merchant/useMerchantName";
import { cn } from "@/common/utils/css.utils";
import { AccountLabels } from "@/components/Account/common/AccountLabels";
import useMerchant from "@/hooks/useMerchant";
import useMe from "@/scenes/auth/hooks/useMe";
import { Heading } from "@/ui/cva/Heading";

type AccountLayoutUserInfoProps = {
  className?: string;
};

/**
 * Welcome block: "Welkom", the name and the account labels (Small).
 * WHO-5954: used in the sidebar (visible with it from md) and at the top of
 * the mobile account menu.
 */
export const AccountLayoutUserInfo = ({
  className,
}: AccountLayoutUserInfoProps) => {
  const { t } = useTranslation();
  const { merchant } = useMerchant();
  const { data } = useMe();
  const merchantName = useMerchantName(merchant, false);
  // WHO-5954 F4: greet by first name, like the Dashboard ("Hoi Geraldine").
  const name = data?.me?.given_name || merchantName;

  return (
    // Not a heading: it renders above the page h1 and would lead the
    // outline (WCAG 1.3.1, WHO-5550). Same visual style through `size`.
    <div
      className={cn("flex min-w-0 flex-col items-start gap-2", className)}
      data-testid="account-welcome-block"
    >
      <Heading as="p" size="h6" className="flex flex-col break-words">
        <span>{t("account:account-menu-welcome")}</span>
        {merchant ? (
          <span>{name}</span>
        ) : (
          // WHO-5954 F3: skeleton while loading instead of "Onbekend".
          <span
            className="skeleton mt-1 block h-5 w-32 rounded"
            data-testid="account-welcome-name-skeleton"
          />
        )}
      </Heading>
      <AccountLabels size="small" />
    </div>
  );
};
